
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { Upload, Loader2, CheckCircle, AlertCircle, X, FileImage, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface KYCDocumentUploadProps {
  onComplete: () => void;
  userProfile: any;
}

const KYCDocumentUpload = ({ onComplete, userProfile }: KYCDocumentUploadProps) => {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState<'front' | 'back' | null>(null);
  const [frontUrl, setFrontUrl] = useState<string | null>(userProfile?.id_front_url || null);
  const [backUrl, setBackUrl] = useState<string | null>(userProfile?.id_back_url || null);
  const [uploadProgress, setUploadProgress] = useState<{ front: number; back: number }>({ front: 0, back: 0 });

  // Initialize existing documents on component mount
  useEffect(() => {
    setFrontUrl(userProfile?.id_front_url || null);
    setBackUrl(userProfile?.id_back_url || null);
  }, [userProfile]);

  const uploadDocument = async (file: File, type: 'front' | 'back') => {
    if (!userProfile?.id) {
      toast.error(t('user_not_authenticated', 'المستخدم غير مصادق عليه', 'User not authenticated'));
      return;
    }

    setUploading(type);
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    
    try {
      console.log(`🔄 Starting ${type} document upload for user:`, userProfile.id);
      
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `${userProfile.id}/${type}_${Date.now()}.${fileExt}`;

      // Upload to storage bucket
      const { error: uploadError } = await supabase.storage
        .from('kyc-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        toast.error(t('upload_failed', 'فشل في رفع الملف', 'Failed to upload file'));
        return;
      }

      setUploadProgress(prev => ({ ...prev, [type]: 50 }));

      // Get the public URL
      const { data } = supabase.storage
        .from('kyc-documents')
        .getPublicUrl(fileName);

      console.log(`📁 Document uploaded to:`, data.publicUrl);

      setUploadProgress(prev => ({ ...prev, [type]: 75 }));

      // Update profile using secure function
      const updateData = type === 'front' 
        ? { id_front_url: data.publicUrl }
        : { id_back_url: data.publicUrl };

      console.log('🔄 Updating profile with secure function:', updateData);

      const { error: updateError } = await supabase.rpc('update_kyc_documents', {
        id_front_url: type === 'front' ? data.publicUrl : null,
        id_back_url: type === 'back' ? data.publicUrl : null
      });

      if (updateError) {
        console.error('❌ Profile update error:', updateError);
        toast.error(t('save_failed', 'فشل في حفظ الوثيقة', 'Failed to save document'));
        
        // Clean up uploaded file if profile update fails
        await supabase.storage
          .from('kyc-documents')
          .remove([fileName]);
        return;
      }

      setUploadProgress(prev => ({ ...prev, [type]: 100 }));

      // Update local state
      if (type === 'front') {
        setFrontUrl(data.publicUrl);
      } else {
        setBackUrl(data.publicUrl);
      }

      console.log(`✅ ${type} document upload completed successfully`);
      
      toast.success(
        type === 'front' 
          ? t('front_uploaded', 'تم رفع الوجه الأمامي بنجاح', 'Front document uploaded successfully')
          : t('back_uploaded', 'تم رفع الوجه الخلفي بنجاح', 'Back document uploaded successfully'),
        {
          description: t('document_under_review', 'سيتم مراجعة الوثيقة من قبل فريق الإدارة', 'Document will be reviewed by the administration team'),
          duration: 4000
        }
      );
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      toast.error(t('upload_error', 'خطأ في رفع الملف', 'Error uploading file'));
    } finally {
      setUploading(null);
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [type]: 0 }));
      }, 2000);
    }
  };

  const handleFileSelect = (type: 'front' | 'back') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(t('file_too_large', 'حجم الملف كبير جداً (الحد الأقصى 5MB)', 'File size too large (max 5MB)'));
          return;
        }
        
        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
          toast.error(t('invalid_file_type', 'نوع الملف غير مدعوم', 'Unsupported file type'));
          return;
        }
        
        uploadDocument(file, type);
      }
    };
    input.click();
  };

  const removeDocument = async (type: 'front' | 'back') => {
    const currentUrl = type === 'front' ? frontUrl : backUrl;
    if (!currentUrl) return;

    try {
      console.log(`🗑️ Removing ${type} document`);
      
      // Update profile to remove document URL
      const { error } = await supabase.rpc('update_kyc_documents', {
        id_front_url: type === 'front' ? '' : null,
        id_back_url: type === 'back' ? '' : null
      });

      if (error) {
        console.error('❌ Error removing document:', error);
        toast.error(t('remove_failed', 'فشل في حذف الوثيقة', 'Failed to remove document'));
        return;
      }

      // Update local state
      if (type === 'front') {
        setFrontUrl(null);
      } else {
        setBackUrl(null);
      }

      toast.success(
        type === 'front' 
          ? t('front_removed', 'تم حذف الوجه الأمامي', 'Front document removed')
          : t('back_removed', 'تم حذف الوجه الخلفي', 'Back document removed')
      );
    } catch (error) {
      console.error('❌ Exception removing document:', error);
      toast.error(t('remove_error', 'خطأ في حذف الملف', 'Error removing file'));
    }
  };

  const canProceed = frontUrl && backUrl;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center flex items-center justify-center gap-2">
          <FileImage className="h-5 w-5" />
          {t('identity_verification', 'التحقق من الهوية', 'Identity Verification')}
        </CardTitle>
        <p className="text-center text-muted-foreground">
          {t('kyc_required_desc', 'يجب رفع صور واضحة لبطاقة الهوية الوطنية من الوجهين لإكمال عملية التحقق', 'Upload clear photos of both sides of your National ID to complete verification')}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Front ID Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium flex items-center gap-2">
              <FileImage className="h-4 w-4" />
              {t('id_front', 'بطاقة الهوية الوجه الأمامي', 'National ID Front')}
              <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center relative overflow-hidden">
              {frontUrl ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-600 font-medium">
                      {t('uploaded', 'مرفوع', 'Uploaded')}
                    </span>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFileSelect('front')}
                      disabled={uploading === 'front'}
                    >
                      {t('replace', 'استبدال', 'Replace')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeDocument('front')}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => handleFileSelect('front')}
                  disabled={uploading === 'front'}
                  className="w-full h-20"
                >
                  {uploading === 'front' ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-sm">{uploadProgress.front}%</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-6 w-6" />
                      <span>{t('upload_front', 'رفع الوجه الأمامي', 'Upload Front')}</span>
                    </div>
                  )}
                </Button>
              )}
              
              {/* Progress bar */}
              {uploading === 'front' && uploadProgress.front > 0 && (
                <div className="absolute bottom-0 left-0 w-full bg-gray-200 h-1">
                  <div 
                    className="bg-primary h-full transition-all duration-300" 
                    style={{ width: `${uploadProgress.front}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Back ID Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium flex items-center gap-2">
              <FileImage className="h-4 w-4" />
              {t('id_back', 'بطاقة الهوية الوجه الخلفي', 'National ID Back')}
              <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center relative overflow-hidden">
              {backUrl ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-600 font-medium">
                      {t('uploaded', 'مرفوع', 'Uploaded')}
                    </span>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFileSelect('back')}
                      disabled={uploading === 'back'}
                    >
                      {t('replace', 'استبدال', 'Replace')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeDocument('back')}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => handleFileSelect('back')}
                  disabled={uploading === 'back'}
                  className="w-full h-20"
                >
                  {uploading === 'back' ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-sm">{uploadProgress.back}%</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-6 w-6" />
                      <span>{t('upload_back', 'رفع الوجه الخلفي', 'Upload Back')}</span>
                    </div>
                  )}
                </Button>
              )}
              
              {/* Progress bar */}
              {uploading === 'back' && uploadProgress.back > 0 && (
                <div className="absolute bottom-0 left-0 w-full bg-gray-200 h-1">
                  <div 
                    className="bg-primary h-full transition-all duration-300" 
                    style={{ width: `${uploadProgress.back}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* File Requirements */}
        <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 mb-2">
            <AlertCircle className="h-4 w-4" />
            <span className="font-medium">
              {t('file_requirements', 'متطلبات الملف', 'File Requirements')}
            </span>
          </div>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <li>• {t('req_clear_image', 'صورة واضحة وعالية الجودة', 'Clear and high-quality image')}</li>
            <li>• {t('req_full_document', 'الوثيقة كاملة وظاهرة بوضوح', 'Full document visible and clear')}</li>
            <li>• {t('req_good_lighting', 'إضاءة جيدة وبدون ظلال', 'Good lighting without shadows')}</li>
            <li>• {t('req_formats', 'الصيغ المدعومة: JPG، PNG، PDF', 'Supported formats: JPG, PNG, PDF')}</li>
            <li>• {t('req_max_size', 'الحد الأقصى للحجم: 5MB', 'Maximum size: 5MB')}</li>
          </ul>
        </div>

        {/* Verification Notice */}
        <div className="bg-amber-50 dark:bg-amber-950 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 mb-2">
            <AlertCircle className="h-4 w-4" />
            <span className="font-medium">
              {t('verification_notice', 'تنبيه التحقق', 'Verification Notice')}
            </span>
          </div>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {t('kyc_verification_desc', 'سيتم مراجعة المستندات خلال 24-48 ساعة عمل. ستحصل على إشعار فور انتهاء المراجعة عبر النظام.', 'Documents will be reviewed within 24-48 business hours. You will receive a notification once the review is complete.')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={onComplete}
            disabled={!canProceed || uploading !== null}
            className="flex-1"
            size="lg"
          >
            {canProceed ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('submit_for_review', 'إرسال للمراجعة', 'Submit for Review')}
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                {t('upload_both_documents', 'ارفع كلا الوثيقتين', 'Upload Both Documents')}
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={onComplete}
            className="flex-shrink-0"
            size="lg"
          >
            {t('cancel', 'إلغاء', 'Cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default KYCDocumentUpload;
